import React, { useMemo } from 'react';
import { IElement } from '@shared/models/project.model';
import { Box } from '@mui/material';
import {
  useElementQuantityProperties,
  useIconQuantityProperties,
} from '@client/hooks/use-quantity-properties.hooks';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  getElementQuantityResolvedCountByName,
  isElementQuantityRepeatingChildName,
} from '@shared/helpers/element-quantity.helpers';
import {
  iconQuantityNames,
  IElementQuantityProperty,
} from '@shared/models/element-quantities.model';
import { ElementPropertiesForm } from './ElementCoreSettings/ElementPropertiesForm';

interface ElementQuantitiesProps {
  element: IElement;
}

const hiddenProperties = new Set<IElementQuantityProperty['name']>([
  'repeating_item_count',
  'repeating_direction',
]);

const iconQuantityNamesSet = new Set<string>(iconQuantityNames);

const ElementQuantities: React.FC<ElementQuantitiesProps> = ({ element }) => {
  const quantityProps = useElementQuantityProperties(element);

  const iconProperties = useIconQuantityProperties(element);

  const otherProperties = useMemo(() => {
    const properties = quantityProps.filter(
      ({ name }) => !iconQuantityNamesSet.has(name),
    );
    const showRepeatingItems = getElementQuantityResolvedCountByName(
      element,
      'repeating_items',
    );

    const quantities = showRepeatingItems
      ? properties.filter(
          ({ name }) => !hiddenProperties.has(name), // hide these for now
        )
      : properties.filter(
          ({ name }) => !isElementQuantityRepeatingChildName(name),
        );

    if (element.category_id === ElementCategoryID.Labour) {
      return quantities.filter((quantity) => quantity.name === 'time');
    }
    if (element.category_id === ElementCategoryID.Energy) {
      return quantities.filter((quantity) => quantity.name === 'energy');
    }
    return quantities.filter((quantity) => quantity.name !== 'time');
  }, [element, quantityProps]);

  if ([...iconProperties, ...otherProperties].length === 0) {
    return null;
  }

  return (
    <Box>
      <ElementPropertiesForm
        element={element}
        properties={iconProperties}
        layout="two-column"
      />
      <ElementPropertiesForm element={element} properties={otherProperties} />
    </Box>
  );
};

export default ElementQuantities;
